import { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ExternalLink, X, ChevronDown } from 'lucide-react';
import type { Source } from '@/types';
import { lightImpact } from '@/utils/haptics';

interface SourceCitationsProps {
  sources: Source[];
}

const SourceCitations = ({ sources }: SourceCitationsProps) => {
  const [expanded, setExpanded] = useState(false);
  const [selectedSource, setSelectedSource] = useState<Source | null>(null);

  if (!sources || sources.length === 0) return null;

  return (
    <div className="mt-2">
      {/* Inline citation badges */}
      <div className="flex flex-wrap gap-1.5">
        {sources.map((source, index) => (
          <motion.button
            key={index}
            whileTap={{ scale: 0.9 }}
            onClick={async () => {
              await lightImpact();
              setSelectedSource(source);
            }}
            className="px-2 py-1 rounded-md bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300
                       text-xs font-semibold hover:bg-primary-100 dark:hover:bg-primary-900/30 transition-colors"
          >
            [{index + 1}]
          </motion.button>
        ))}
        <motion.button
          whileTap={{ scale: 0.9 }}
          onClick={() => setExpanded(!expanded)}
          className="flex items-center gap-1 px-2 py-1 rounded-md text-xs text-slate-400 hover:text-slate-600 transition-colors"
        >
          <ChevronDown className={`w-3 h-3 transition-transform ${expanded ? 'rotate-180' : ''}`} />
          {expanded ? 'Hide' : 'Show all'}
        </motion.button>
      </div>

      {/* Expanded list */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="mt-2 bg-slate-50 dark:bg-slate-800/50 rounded-xl p-3 border border-slate-200 dark:border-slate-700 space-y-2">
              {sources.map((source, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedSource(source)}
                  className="w-full flex items-start gap-2 text-left p-2 rounded-lg hover:bg-white dark:hover:bg-slate-700/50 transition-colors"
                >
                  <span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400
                                   text-[10px] font-bold flex items-center justify-center mt-0.5">
                    {index + 1}
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-700 dark:text-slate-200 truncate">
                      {source.title}
                    </p>
                    <p className="text-[11px] text-slate-400 truncate">{source.url}</p>
                  </div>
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Bottom sheet for selected source */}
      <AnimatePresence>
        {selectedSource && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 backdrop-blur-sm"
            onClick={() => setSelectedSource(null)}
          >
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-lg bg-white dark:bg-slate-900 rounded-t-3xl p-6 shadow-2xl"
            >
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-lg font-bold text-slate-900 dark:text-white pr-4">
                  {selectedSource.title}
                </h3>
                <motion.button
                  whileTap={{ scale: 0.9 }}
                  onClick={() => setSelectedSource(null)}
                  className="p-2 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 flex-shrink-0 min-h-12 min-w-12 flex items-center justify-center"
                >
                  <X className="w-5 h-5 text-slate-500" />
                </motion.button>
              </div>

              {selectedSource.snippet && (
                <p className="text-sm text-slate-600 dark:text-slate-300 mb-4 leading-relaxed">
                  {selectedSource.snippet}
                </p>
              )}

              <a
                href={selectedSource.url}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl
                           gradient-bg text-white font-semibold text-sm shadow-glow min-h-12"
              >
                <ExternalLink className="w-4 h-4" />
                Open Source
              </a>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SourceCitations;
